import type { AxiosRequestConfig } from 'axios';
import type { MarketRelease } from '@/types';

export interface SkillUploadIdentity {
  itemKey: string;
  version: string;
  artifactSha256: string;
  artifactSizeBytes: string;
}
export interface SkillPublishRequest extends SkillUploadIdentity {
  displayName: Record<string, string>;
  displayDescription: Record<string, string>;
  iconUrl: string;
  minimumHostVersion: string;
  agentProviders: string[];
}
export interface SkillUploadSession {
  uploadUrl: string;
  headers: Record<string, string>;
  expiresAtMs: string;
}
export interface SkillPublishReply {
  release: MarketRelease;
  created: boolean;
}
export interface SkillUploadApi {
  createSession(
    input: SkillUploadIdentity,
    signal: AbortSignal,
  ): Promise<SkillUploadSession>;
  putFile(
    session: SkillUploadSession,
    file: File,
    signal: AbortSignal,
  ): Promise<void>;
  publish(
    input: SkillPublishRequest,
    signal: AbortSignal,
  ): Promise<SkillPublishReply>;
}
interface HttpClient {
  post<T>(
    path: string,
    body: unknown,
    options?: AxiosRequestConfig,
  ): Promise<T>;
}
const base = '/api/v1/admin/market/tutti-server/skills';

// Signed object PUTs intentionally bypass the authenticated admin transport.
export async function putSignedSkillFile(
  session: SkillUploadSession,
  file: File,
  signal: AbortSignal,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  const url = new URL(session.uploadUrl);
  if (url.protocol !== 'https:' || url.username || url.password || url.hash) {
    throw new Error('上传地址无效，请重新获取上传签名');
  }
  if (
    !/^\d+$/.test(session.expiresAtMs) ||
    Number(session.expiresAtMs) <= Date.now()
  ) {
    throw new Error('上传签名已过期，请重试');
  }
  const headers = new Headers(session.headers);
  if (
    headers.get('content-type') !== 'application/zip' ||
    headers.get('if-none-match') !== '*'
  ) {
    throw new Error('上传签名缺少必要的文件约束');
  }
  const response = await fetcher(url.href, {
    method: 'PUT',
    body: file,
    headers,
    signal,
    credentials: 'omit',
    redirect: 'error',
  });
  // A previous attempt may already have uploaded these bytes. Only publish can
  // verify the existing object; a 412 must never be shown as publication success.
  if (!response.ok && response.status !== 412) {
    throw new Error(`文件上传失败（HTTP ${response.status}），请重试`);
  }
}

export function createSkillUploadApi(
  http: HttpClient,
  putFile: SkillUploadApi['putFile'] = putSignedSkillFile,
): SkillUploadApi {
  return {
    createSession: (input, signal) =>
      http.post(`${base}/upload-sessions`, input, { signal }),
    putFile,
    publish: (input, signal) =>
      http.post(`${base}/releases:publish`, input, { signal }),
  };
}
